import {
  AutoAssignMaterialConfigurationType,
  Co2MaterialType,
  CreateFilteredElementConfigurationRequest,
  CreateModelCommandRequest,
  FilteredElementConfigurationType,
  ModelCommandType,
  ProfileLevelType,
  UpdateFilteredElementConfigurationRequest,
} from "@api/apiClient";
import { useCadMaterials } from "@api/cadMaterials";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useCreateModelCommand, useUpdateModelCommand } from "@api/modelCommand";
import { useProfileSearch } from "@api/profiles";
import { useCreateFilteredElementConfiguration, useUpdateFilteredElementConfiguration } from "@api/queryBuilders";
import MaterialSelectDialog from "@components/MaterialSelectDialog/MaterialSelectDialog";
import i18n from "@i18n/i18n";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  createFilterOptions,
  FilterOptionsState,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { observer } from "mobx-react";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface AutoMappingDropdownModel {
  label: string;
  value: string;
}

interface AutoMappingFormData {
  autoAssignMaterialConfigurationType: AutoAssignMaterialConfigurationType; // labelName
  commandId?: string | null;
  operatorName: string;
  values: AutoMappingDropdownModel[] | string;
  description: string;
  co2MaterialType: any; // will be having values of Co2MaterialType;
  filteredElementConfiguration: any; // Since it will be a JSON
  filterConfigId: string;
  profileName?: string | undefined;
  profileId?: string;
}

export interface AutoMappingFormProps {
  onFormSuccess: (isForEdit: boolean) => void;
  modelCommandDetail: any;
  clearModelCommandDetail: () => void;
}

const AutoMappingForm = observer((props: AutoMappingFormProps) => {
  const { modelCommandDetail, onFormSuccess, clearModelCommandDetail } = props;

  const initialFormData: AutoMappingFormData = {
    autoAssignMaterialConfigurationType: AutoAssignMaterialConfigurationType.CadMaterial,
    operatorName: "",
    values: [],
    description: "",
    co2MaterialType: {},
    filteredElementConfiguration: null,
    filterConfigId: "",
    profileName: "",
    profileId: "",
  };

  const operatorDropdown: AutoMappingDropdownModel[] = [
    {
      label: "in",
      value: i18n.t("auto-mapping.add-auto-mapping-dialog.in"),
    },
  ];

  // Hooks
  const createModelCommand = useCreateModelCommand();
  const updateModelCommand = useUpdateModelCommand(modelCommandDetail?.commandId);
  const cadModelCategories = useCadModelCategories();
  const cadMaterials = useCadMaterials();
  const profiles = useProfileSearch([ProfileLevelType.Application], null);
  const createFilteredElementConfiguration = useCreateFilteredElementConfiguration();
  const updateFilteredElementConfiguration = useUpdateFilteredElementConfiguration();

  const [materialsDropdownValue, setMaterialsDropdownValue] = useState<AutoAssignMaterialConfigurationType>(AutoAssignMaterialConfigurationType.CadMaterial);

  const [modelCommandType] = useState(ModelCommandType.AutoAssignMaterial);
  const [newEcMaterialName, setNewEcMaterialName] = useState<string | null>(null);

  const [isCustom, setIsCustom] = useState<boolean>(false);
  const [valueDropdown, setValueDropdown] = useState<AutoMappingDropdownModel[]>([]);
  const [selectedValues, setSelectedValues] = useState<AutoMappingDropdownModel[]>([]);
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");
  const [selectedOperator, setSelectedOperator] = useState<string>(operatorDropdown[0].value);
  const [openMaterialDlg, setOpenMaterialDlg] = useState<boolean>(false);
  const [formData, setFormData] = useState<AutoMappingFormData>({ ...initialFormData });
  const [co2MaterialError, setCo2MaterialError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEdit = useMemo(() => !!modelCommandDetail, [modelCommandDetail]);

  const profileOptions: AutoMappingDropdownModel[] = useMemo(
    () =>
      profiles.data?.map((profile) => ({
        label: profile.name!,
        value: profile.profileId!,
      })) ?? [],
    [profiles.data],
  );

  const cadMaterialOptions: AutoMappingDropdownModel[] = useMemo(() => cadMaterials.data?.map((m) => ({ label: m, value: m })) ?? [], [cadMaterials.data]);

  const cadModelCategoryOptions: AutoMappingDropdownModel[] = useMemo(
    () => cadModelCategories.data?.map((cat) => ({ label: cat.label!, value: cat.cadModelCategoryId! })) ?? [],
    [cadModelCategories.data],
  );

  useEffect(() => {
    if (!isEdit && profileOptions.length > 0) {
      const def = profileOptions[0];
      setSelectedProfileValue(def.value);
      setFormData((prev) => ({ ...prev, profileId: def.value, profileName: def.label }));
    }
  }, [profileOptions, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    console.trace("first switch before", selectedValues);
    switch (materialsDropdownValue) {
      case AutoAssignMaterialConfigurationType.CadCustom:
        setIsCustom(true);
        setValueDropdown([]);
        break;
      case AutoAssignMaterialConfigurationType.CadMaterial:
        setIsCustom(false);
        setValueDropdown([...cadMaterialOptions]);
        break;
      case AutoAssignMaterialConfigurationType.CadModelCategory:
        setIsCustom(false);
        setValueDropdown([...cadModelCategoryOptions]);
        break;
      default:
        setIsCustom(false);
        setValueDropdown([]);
        break;
    }
    console.trace("first switch After", selectedValues);
  }, [materialsDropdownValue, cadMaterialOptions, cadModelCategoryOptions, isEdit]);

  useEffect(() => {
    if (!modelCommandDetail) return;

    const matType = modelCommandDetail.name as AutoAssignMaterialConfigurationType;

    setMaterialsDropdownValue(matType);

    const base = matType === AutoAssignMaterialConfigurationType.CadModelCategory ? cadMaterialOptions : cadMaterialOptions;

    const customValues: AutoMappingDropdownModel[] = (modelCommandDetail.values as string[])
      .filter((val) => !base.find((x) => x.value === val))
      .map((val) => ({ label: val, value: val }));

    const mergedDropdown = [...customValues, ...base];
    setValueDropdown(mergedDropdown);

    const newSelected = mergedDropdown.filter((value) => (modelCommandDetail.values as string[]).filter((v) => v != null || v != undefined || v != "").includes(value.value));
    setSelectedValues(newSelected);

    setSelectedOperator(modelCommandDetail.operatorName);

    if (modelCommandDetail.profileId) setSelectedProfileValue(modelCommandDetail.profileId);

    setNewEcMaterialName(modelCommandDetail.co2MaterialType?.name ?? null);

    if (modelCommandDetail.co2MaterialType) {
      setFormData((prev) => ({ ...prev, co2MaterialType: modelCommandDetail.co2MaterialType }));
      setCo2MaterialError(false);
    }

    setFormData((prev) => ({
      ...prev,
      operatorName: modelCommandDetail.operatorName,
      values: newSelected,
      profileId: modelCommandDetail.profileId,
      profileName: modelCommandDetail.profile?.name,
      autoAssignMaterialConfigurationType: matType,
    }));
  }, [modelCommandDetail, cadMaterialOptions, cadModelCategoryOptions]);

  const materialsDropdownOptions: AutoMappingDropdownModel[] = [
    // {
    //   label: i18n.t("rule-set-filter-form.form-elements.cad-model-category"),
    //   value: AutoAssignMaterialConfigurationType.CadModelCategory,
    // },
    {
      label: i18n.t("rule-set-filter-form.form-elements.cad-material"),
      value: AutoAssignMaterialConfigurationType.CadMaterial,
    },
    //{
    //  label: i18n.t("rule-set-filter-form.form-elements.cad-material-text"),
    //  value: AutoAssignMaterialConfigurationType.CadCustom,
    //},
  ];

  const updateMaterialTypeDropDown = useCallback(
    (value: AutoAssignMaterialConfigurationType) => {
      setIsCustom(false);
      console.trace("Before", selectedValues);
      switch (value) {
        case AutoAssignMaterialConfigurationType.CadCustom:
          setIsCustom(true);
          setValueDropdown([]);
          break;
        case AutoAssignMaterialConfigurationType.CadMaterial:
          setValueDropdown([...cadMaterialOptions]);
          break;
        case AutoAssignMaterialConfigurationType.CadModelCategory:
          setValueDropdown([...cadModelCategoryOptions]);
          break;
      }
      console.trace("After", selectedValues);
    },
    [cadMaterialOptions, cadModelCategoryOptions],
  );

  const handleMaterialDropdownChange = useCallback(
    (event: SelectChangeEvent<AutoAssignMaterialConfigurationType>) => {
      const autoAssignMaterialConfigurationType = event.target.value as AutoAssignMaterialConfigurationType;

      console.trace("Called this method", selectedValues);
      setSelectedValues([]);

      if (!isEdit) {
        resetForm(false);
      }

      setMaterialsDropdownValue(autoAssignMaterialConfigurationType);
      updateMaterialTypeDropDown(autoAssignMaterialConfigurationType);
      setFormData({ ...formData, autoAssignMaterialConfigurationType, values: "" });
    },
    [isEdit, updateMaterialTypeDropDown],
  );

  const handleOperatorChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedOperator(value);
    setFormData((prev) => ({ ...prev, operatorName: value }));
  }, []);

  const handleCancel = useCallback(() => {
    resetForm(true);
  }, []);

  const handleProfileChange = useCallback(
    (event: SelectChangeEvent) => {
      const { value } = event.target;
      setSelectedProfileValue(value as string);

      const name = profileOptions.find((x) => x.value === value);

      setFormData((prev) => ({ ...prev, profileId: value, profileName: name?.label }));
    },
    [profileOptions],
  );

  const onMaterialValueChange = useCallback(
    (_event: SyntheticEvent, newValue: any) => {
      if (newValue.length > 0) {
        const addValue = newValue.at(-1);
        const isAddValue = addValue.label === `Add ${addValue.value}`;
        if (isAddValue) {
          const normalized = { label: addValue.value, value: addValue.value };
          // check for duplicate
          const exists = valueDropdown.some((v) => v.value === normalized.value);
          const nextValue = exists ? valueDropdown : [normalized, ...valueDropdown];
          setValueDropdown(nextValue);

          newValue = [...newValue.slice(0, -1), normalized];
        }
      }
      setSelectedValues(newValue);
      setFormData((prev) => ({ ...prev, values: newValue }));
    },
    [valueDropdown],
  );

  const onInputMaterialValueChange = useCallback((event: SelectChangeEvent<any>) => {
    setFormData((prev) => ({ ...prev, values: event.target.value }));
  }, []);

  const handleSelectMaterial = useCallback((selectedMaterialType?: Co2MaterialType) => {
    console.trace("inside handle select material", selectedValues);
    if (selectedMaterialType) {
      setFormData((prev) => ({
        ...prev,
        co2MaterialType: selectedMaterialType,
      }));
      setNewEcMaterialName(selectedMaterialType.name ?? null);
      setCo2MaterialError(false);
    }
    console.trace("end of handle select material", selectedValues);
    setOpenMaterialDlg(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();

      if (!formData.co2MaterialType?.name && !newEcMaterialName) {
        setCo2MaterialError(true);
        return;
      } else {
        setCo2MaterialError(false);
      }

      const materialType = formData.autoAssignMaterialConfigurationType ?? materialsDropdownValue;

      const formPayload = {
        data: {
          and: [
            {
              in: [{ var: materialType }, Array.isArray(formData.values) ? formData.values.map((value) => value.value) : (formData.values as string).split(",")],
            },
          ],
        },
        co2MaterialTypeId: formData.co2MaterialType?.co2MaterialTypeId,
      };

      let filterModelResponse: any;

      try {
        setIsSubmitting(true);

        if (modelCommandDetail?.filteredElementConfigurationId) {
          const updateFilteredElementConfigurationRequest = new UpdateFilteredElementConfigurationRequest({
            name: formData.autoAssignMaterialConfigurationType,
            description: formData.autoAssignMaterialConfigurationType,
            configuration: JSON.stringify(formPayload),
            filteredElementConfigurationType: FilteredElementConfigurationType.AutoAssignMaterial,
          });

          const data = {
            filteredElementConfigurationId: modelCommandDetail.filteredElementConfigurationId,
            updateFilteredElementConfigurationRequest,
          };

          filterModelResponse = await updateFilteredElementConfiguration.mutateAsync(data as any);
        } else {
          const createFilteredElementConfigurationRequest = new CreateFilteredElementConfigurationRequest({
            name: materialType,
            description: materialType,
            configuration: JSON.stringify(formPayload),
            filteredElementConfigurationType: FilteredElementConfigurationType.AutoAssignMaterial,
          });

          filterModelResponse = await createFilteredElementConfiguration.mutateAsync(createFilteredElementConfigurationRequest as any);
        }

        if (filterModelResponse?.filteredElementConfigurationId) {
          setFormData((prevData: any) => ({
            ...prevData,
            filterConfigId: filterModelResponse.filteredElementConfigurationId,
          }));
        }

        if (String(materialType ?? "").trim().length > 0) {
          const commandRequest = createPayload({
            ...formData,
            filterConfigId: filterModelResponse?.filteredElementConfigurationId,
          });

          if (commandRequest) {
            if (isEdit) {
              await updateModelCommand.mutateAsync(commandRequest as any);
              onFormSuccess(true);
            } else {
              await createModelCommand.mutateAsync(commandRequest as any);
              onFormSuccess(false);
            }

            resetForm(true);
            onFormSuccess(isEdit);
          } else {
            console.error("Command request could not be created.");
          }
        }
      } catch (error) {
        console.error("Error during submission:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      newEcMaterialName,
      materialsDropdownValue,
      modelCommandDetail,
      isEdit,
      updateFilteredElementConfiguration,
      createFilteredElementConfiguration,
      updateModelCommand,
      createModelCommand,
      props,
    ],
  );

  const resetForm = useCallback(
    (cleanModelDetail: boolean) => {
      setIsCustom(false);
      setSelectedOperator(i18n.t("auto-mapping.add-auto-mapping-dialog.in"));
      setSelectedValues([]);
      setValueDropdown([]);
      setCo2MaterialError(false);

      setMaterialsDropdownValue(AutoAssignMaterialConfigurationType.CadMaterial);
      updateMaterialTypeDropDown(AutoAssignMaterialConfigurationType.CadMaterial);

      if (profileOptions.length > 0) {
        const defaultProfile = profileOptions[0];
        setSelectedProfileValue(defaultProfile.value);
        setFormData({
          ...initialFormData,
          profileId: defaultProfile.value,
          profileName: defaultProfile.label,
        });
      } else {
        setSelectedProfileValue("");
        setFormData({ ...initialFormData });
      }
      setNewEcMaterialName(null);
      if (cleanModelDetail) clearModelCommandDetail();
    },
    [profileOptions, updateMaterialTypeDropDown, props],
  );

  useEffect(() => {
    if (modelCommandDetail) {
      const values = modelCommandDetail.values ? modelCommandDetail.values.map((x: any) => ({ label: x, value: x })) : [];
      setFormData((prev) => ({
        ...prev,
        autoAssignMaterialConfigurationType: modelCommandDetail.name,
        operatorName: modelCommandDetail.operatorName,
        values: values,
        profileId: modelCommandDetail.profileId,
        profileName: modelCommandDetail.profileName,
        co2MaterialType: modelCommandDetail.co2MaterialType,
        filterConfigId: modelCommandDetail.filteredElementConfigurationId,
        description: modelCommandDetail.description ?? "",
        filteredElementConfiguration: modelCommandDetail.arguments ? JSON.parse(modelCommandDetail.arguments) : {},
        commandId: modelCommandDetail.commandId,
      }));

      setSelectedOperator(modelCommandDetail.operatorName);
      setSelectedValues(values);
      setSelectedProfileValue(modelCommandDetail.profileId || "");
      setNewEcMaterialName(modelCommandDetail.co2MaterialType?.name ?? null);
      setCo2MaterialError(false);
      updateMaterialTypeDropDown(modelCommandDetail.name);
    } else {
      if (!selectedValues || selectedValues.length === 0) {
        resetForm(false);
      }
    }
  }, [modelCommandDetail, resetForm]);

  function createPayload(formData: any): CreateModelCommandRequest | undefined {
    try {
      return new CreateModelCommandRequest({
        name: "AutoAssignCommand",
        description: formData.description,

        arguments: JSON.stringify({
          filteredElementConfigurationId: formData.filterConfigId,
          co2MaterialTypeId: formData.co2MaterialType?.co2MaterialTypeId,
          value: formData.value,
        }),
        command: modelCommandType,
        profileId: formData.profileId,
      });
    } catch (error) {
      console.error("Error creating payload:", error);
      return undefined;
    }
  }

  useEffect(() => {
    console.trace("update in selected value", selectedValues);
  }, [selectedValues]);

  return (
    <Box sx={{ p: 2, border: 1, width: "100%" }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={3}>{i18n.t("auto-mapping.add-auto-mapping-dialog.cad-material-category")}</Grid>
          <Grid size={3}>
            <FormControl fullWidth>
              <InputLabel id="field-label">{i18n.t("auto-mapping.add-auto-mapping-dialog.select-field")}</InputLabel>
              <Select
                labelId="field-label"
                id="field-select"
                value={materialsDropdownValue}
                onChange={handleMaterialDropdownChange}
                input={<OutlinedInput label={i18n.t("auto-mapping.add-auto-mapping-dialog.select-field")} />}
                MenuProps={MenuProps}
              >
                {Object.values(materialsDropdownOptions).map((field) => (
                  <MenuItem key={field.value} value={field.value}>
                    {field.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={1}>
            <FormControl fullWidth>
              <InputLabel id="operator-label">{i18n.t("auto-mapping.add-auto-mapping-dialog.select-operator")}</InputLabel>
              <Select
                labelId="operator-label"
                id="operator-select"
                value={selectedOperator}
                onChange={handleOperatorChange}
                input={<OutlinedInput label={i18n.t("auto-mapping.add-auto-mapping-dialog.select-operator")} />}
              >
                {operatorDropdown.map((operator) => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={5}>
            {isCustom ? (
              <FormControl fullWidth required>
                <TextField
                  id="outlined-basic"
                  label={i18n.t("auto-mapping.add-auto-mapping-dialog.select-value")}
                  value={formData.values}
                  variant="outlined"
                  onChange={() => onInputMaterialValueChange}
                />
              </FormControl>
            ) : (
              <FormControl fullWidth required>
                <Autocomplete
                  freeSolo
                  id="material-value-select"
                  multiple
                  disableClearable
                  options={valueDropdown}
                  value={selectedValues}
                  disableCloseOnSelect
                  onChange={onMaterialValueChange}
                  renderOption={(props, option, state) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox icon={<CheckBoxOutlineBlankIcon />} checkedIcon={<CheckBoxIcon />} style={{ marginRight: 8 }} checked={state.selected} />
                        {option.label}
                      </li>
                    );
                  }}
                  renderInput={(params) => <TextField {...params} variant="outlined" label={i18n.t("auto-mapping.add-auto-mapping-dialog.select-value")} />}
                  filterOptions={(options: AutoMappingDropdownModel[], params: FilterOptionsState<AutoMappingDropdownModel>) => {
                    let filtered: AutoMappingDropdownModel[] = options;

                    if (params.inputValue !== "") {
                      const match = new RegExp(params.inputValue, "i");
                      filtered = filtered.filter((value) => match.test(value.label));

                      const exactMatch = options.find((value) => value.label === params.inputValue);

                      if (filtered.length === 0 || !exactMatch) {
                        filtered = [{ label: `Add ${params.inputValue}`, value: params.inputValue }, ...filtered];
                      }
                    }

                    return filtered;
                  }}
                />
              </FormControl>
            )}
          </Grid>
        </Grid>

        <Grid mt={2} container spacing={3} alignItems="center">
          <Grid size={3}>{i18n.t("auto-mapping.add-auto-mapping-dialog.ec-material-text")}</Grid>
          <Grid size={9}>
            <FormControl fullWidth error={co2MaterialError}>
              <TextField
                fullWidth
                value={formData.co2MaterialType?.name || newEcMaterialName || i18n.t("auto-mapping.add-auto-mapping-dialog.select-ec-material")}
                sx={{ "& .MuiInputBase-input": { cursor: "pointer" } }}
                onClick={() => setOpenMaterialDlg(true)}
                label={i18n.t("auto-mapping.add-auto-mapping-dialog.ec-material")}
                placeholder={i18n.t("auto-mapping.add-auto-mapping-dialog.select-ec-material")}
                helperText={co2MaterialError && i18n.t("auto-mapping.add-auto-mapping-dialog.ecMaterial-validation")}
                slotProps={{
                  input: { readOnly: true },
                  formHelperText: {
                    sx: { color: co2MaterialError ? "red" : "inherit" },
                  },
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

        {openMaterialDlg && (
          <MaterialSelectDialog open={openMaterialDlg} onClose={() => handleSelectMaterial()} onSubmit={(selectedMaterialType) => handleSelectMaterial(selectedMaterialType)} />
        )}

        <Grid mt={2} container spacing={3} alignItems="center">
          <Grid size={3}>{i18n.t("auto-mapping.add-auto-mapping-dialog.profile-label")}</Grid>
          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel id="project-type-label">{i18n.t("auto-mapping.add-auto-mapping-dialog.profile-label")}</InputLabel>
              <Select
                labelId="project-type-label"
                id="project-type-select"
                value={selectedProfileValue}
                onChange={handleProfileChange}
                input={<OutlinedInput label={i18n.t("auto-mapping.add-auto-mapping-dialog.select-profile")} />}
              >
                {profileOptions.map((profile) => (
                  <MenuItem key={profile.value} value={profile.value}>
                    {profile.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={3}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }} mt={2}>
              <Button variant="outlined" sx={{ mx: 1 }} onClick={handleCancel}>
                {i18n.t("non-model-element.add-non-model-element-dialog.cancel")}
              </Button>
              <Button autoFocus variant="contained" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : i18n.t("non-model-element.add-non-model-element-dialog.save")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
});

const filter = createFilterOptions<AutoMappingDropdownModel>();

export default AutoMappingForm;
